import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, ChevronDown, Camera, Sparkles, Leaf, DollarSign, MapPin } from 'lucide-react';

const HelpScreen = () => {
  const { setCurrentScreen } = useApp();
  const [openSection, setOpenSection] = useState(null);

  const faqSections = [
    {
      title: 'Getting Started',
      icon: Camera,
      questions: [
        {
          q: 'How do I scan a garment?',
          a: 'Tap the SCAN tab, then capture photos of clothing labels and the fabric itself. The AI will analyze composition, quality, and sustainability.'
        },
        {
          q: 'What images should I capture?',
          a: 'Take clear photos of: 1) Care labels with composition details, 2) Fabric texture and weave, 3) Brand tags. Multiple angles help improve accuracy.'
        },
        {
          q: 'How long does analysis take?',
          a: 'Most scans complete in 15-30 seconds. Our AI analyzes fabric composition, searches for pricing data, and generates sustainability insights.'
        }
      ]
    },
    {
      title: 'Understanding Scores',
      icon: Sparkles,
      questions: [
        {
          q: 'What is the Fabric Intelligence score?',
          a: 'Rates breathability, durability, and comfort from 1-5. Based on material composition, weave patterns, and fabric properties.'
        },
        {
          q: 'How is Sustainability calculated?',
          a: 'Considers water usage, carbon footprint, chemical treatments, manufacturing ethics, and brand practices. Higher scores mean lower environmental impact.'
        },
        {
          q: 'What does Price Fairness mean?',
          a: 'Compares the garment\'s price to market averages for similar materials and categories. Shows if you\'re getting fair value.'
        }
      ]
    },
    {
      title: 'Shopping Location',
      icon: MapPin,
      questions: [
        {
          q: 'Why set a shopping location?',
          a: 'Your location determines currency, regional retailers, and seasonal fabric recommendations. We support India, USA, UK, and UAE.'
        },
        {
          q: 'Can I change my location?',
          a: 'Yes! Go to Profile → Shopping Location to select a different country anytime. All prices will update accordingly.'
        }
      ]
    },
    {
      title: 'Account & Privacy',
      icon: User,
      questions: [
        {
          q: 'What data do you collect?',
          a: 'We store your scans, preferences, and followed brands. All data is encrypted and never shared with third parties.'
        },
        {
          q: 'Can I delete my account?',
          a: 'Yes. Go to Profile → Settings → Delete Account. This permanently removes all your data from our servers.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-4">
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <img src="/logos/logo-black.jpg" alt="ATELIA" className="h-24 w-auto object-contain" />
      </div>

      <div className="px-6 pb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-2 font-bold">SUPPORT</p>
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Help Center</h1>
      </div>

      {/* FAQ Sections */}
      <div className="px-6 space-y-3">
        {faqSections.map((section, sIdx) => {
          const Icon = section.icon;
          return (
            <div key={sIdx} className="bg-white border border-black/12 rounded-xl overflow-hidden">
              {/* Section Header */}
              <button
                onClick={() => setOpenSection(openSection === sIdx ? null : sIdx)}
                className="w-full p-4 flex items-center justify-between hover:bg-black/5 transition-all"
              >
                <div className="flex items-center gap-3">
                  <Icon size={18} className="text-black" />
                  <p className="text-sm font-bold text-black uppercase">{section.title}</p>
                </div>
                <ChevronDown 
                  size={16} 
                  className={`text-black/55 transition-transform ${
                    openSection === sIdx ? 'rotate-180' : ''
                  }`} 
                />
              </button>

              {/* Questions */}
              {openSection === sIdx && (
                <div className="border-t border-black/12">
                  {section.questions.map((item, qIdx) => (
                    <div key={qIdx} className="p-4 border-b border-black/12 last:border-0">
                      <p className="text-sm font-bold text-black mb-2">{item.q}</p>
                      <p className="text-sm text-black/72 leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Contact Button */}
      <div className="px-6 mt-6">
        <button
          onClick={() => setCurrentScreen('contact-us')}
          className="w-full bg-black text-white py-4 rounded-xl font-bold uppercase
            hover:bg-black/90 transition-all"
        >
          Still Need Help? Contact Us
        </button>
      </div>
    </div>
  );
};

export default HelpScreen;